import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import { RightOutlined }         from '@ant-design/icons';
import View                      from '@widesk-ui/components/View';
import Alert                     from '@widesk-ui/components/Alert';
import Button                    from '@widesk-ui/components/Button';
import SelectPlaceType           from '@selects/map/SelectPlaceType';
import useDesignTokens           from '@/hooks/useDesignTokens';
import PlanDoubtCard             from '@/TokamapEditor/planImport/PlanDoubtCard';
import PlanLayerList            from '@/TokamapEditor/planImport/PlanLayerList';
import PlanMatchChoice           from '@/TokamapEditor/planImport/PlanMatchChoice';
import { doubtKey }              from '@/TokamapEditor/helpers/planDoubts';
import { previewKey }            from '@/TokamapEditor/helpers/planLayerMapping';
import { isShapeEnabled }        from '@/TokamapEditor/helpers/planShapes';
import { planShapeState }        from '@/TokamapEditor/helpers/planShapes';
import { shapeOwner }            from '@/TokamapEditor/helpers/planShapes';
import { PLAN_SHAPE_LEGEND }     from '@/TokamapEditor/helpers/planShapes';
import { PLAN_SHAPE_STYLES }     from '@/TokamapEditor/helpers/planShapes';
import type { PlanShapeState }   from '@/TokamapEditor/helpers/planShapes';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import planImportStore           from '@/TokamapEditor/planImport/planImportStore';
import PlanShapesCanvas          from '@/TokamapEditor/planImport/PlanShapesCanvas';
import type { PlanShapeEntry }   from '@/TokamapEditor/planImport/PlanShapesCanvas';

const PlanReviewStep = observer(() => {
	const tokens = useDesignTokens();
	const [previewed, setPreviewed] = React.useState<string | null>(null);
	const [focusRequest, setFocusRequest] = React.useState(0);
	const [isolated, setIsolated] = React.useState<PlanShapeState | null>(null);
	const [frameRequest, setFrameRequest] = React.useState<{ keys: string[]; nonce: number } | null>(null);
	const [confirming, setConfirming] = React.useState(false);
	const [advancedOpen, setAdvancedOpen] = React.useState(false);

	const { detection, arbitrations, currentDoubtIndex, activeDoubtIndexes } = planImportStore;
	const categories = planImportStore.effectiveCategories;

	const entries = React.useMemo<PlanShapeEntry[]>(() => {
		if (!detection) return [];

		const doubtful = new Set(activeDoubtIndexes.map(index => doubtKey(detection.doubts[index]!)));

		return detection.shapes.map(shape => {
			const key = doubtKey(shape);
			const enabled = isShapeEnabled(categories, shape.layer, shape.index);
			const state = planShapeState({ shape, enabled, doubtful: doubtful.has(key) && undefined === arbitrations[key] });

			return { shape, key, enabled, state, style: PLAN_SHAPE_STYLES[state] };
		});
	}, [detection, categories, arbitrations, activeDoubtIndexes]);

	const highlighted = React.useMemo(() => new Set(previewed
		? entries.filter(entry => {
			const owner = shapeOwner(categories, entry.shape.layer, entry.shape.index);

			return (owner && previewKey(owner) === previewed) || entry.shape.layer === previewed;
		}).map(entry => entry.key)
		: []), [entries, previewed, categories]);

	if (!detection) return null;

	const counts = entries.reduce<Record<PlanShapeState, number>>(
		(total, entry) => ({ ...total, [entry.state]: total[entry.state] + 1 }),
		{ enabled: 0, doubtful: 0, unsupported: 0, discarded: 0 },
	);
	const shown = isolated && counts[isolated] ? isolated : null;
	const remaining = planImportStore.remainingDoubts;
	const current = null === currentDoubtIndex ? null : detection.doubts[currentDoubtIndex] ?? null;
	const projectUrn = currentProjectEditorStore.get.project.urn;

	const togglePreview = (key: string) => setPreviewed(previewed === key ? null : key);

	const toggleAdvanced = () => {
		if (advancedOpen) setPreviewed(null);
		setAdvancedOpen(!advancedOpen);
	};

	const keysOf = (state: PlanShapeState) => entries.filter(entry => entry.state === state).map(entry => entry.key);

	const pickCounter = (state: PlanShapeState) => {
		setConfirming(false);

		if (shown === state) {
			setIsolated(null);

			return;
		}

		setIsolated(state);
		setFrameRequest({ keys: keysOf(state), nonce: (frameRequest?.nonce ?? 0) + 1 });
	};

	const unsupportedShapes = entries.filter(entry => 'unsupported' === entry.state).map(entry => entry.shape);
	const manualDiscards = planImportStore.manualDiscardCount;

	return (
		<View row flex style={{ minHeight: 0 }}>
			<PlanShapesCanvas
				source={detection.source}
				entries={entries}
				current={current}
				doubts={activeDoubtIndexes.map((index, position) => {
					const doubt = detection.doubts[index]!;

					return { key: doubtKey(doubt), index, number: position + 1, settled: undefined !== arbitrations[doubtKey(doubt)] };
				})}
				focusRequest={focusRequest}
				isolated={shown}
				frameRequest={frameRequest}
				highlighted={highlighted}
				onArbitrate={(shape, decision) => planImportStore.arbitrate(shape, decision)}
				onGoDoubt={index => planImportStore.goToDoubt(index)}
			/>

			<View style={{ width: 420, borderLeft: `1px solid ${tokens.border.subtle}`, minHeight: 0 }}>
				<View gap={12} style={{ padding: '16px 20px 0' }}>
					<View row gap={8}>
						{PLAN_SHAPE_LEGEND.map(({ state, label, hint }) => {
							const active = shown === state;

							return (
								<button
									key={state}
									type="button"
									title={hint}
									aria-pressed={active}
									disabled={!counts[state]}
									onClick={() => pickCounter(state)}
									style={{
										flex: 1,
										minWidth: 0,
										display: 'flex',
										flexDirection: 'column',
										gap: 2,
										textAlign: 'left',
										font: 'inherit',
										color: 'inherit',
										cursor: counts[state] ? 'pointer' : 'default',
										opacity: counts[state] ? 1 : 0.5,
										padding: '8px 10px',
										borderRadius: 8,
										border: `1px solid ${active ? tokens.color.primary : 'transparent'}`,
										background: active ? `color-mix(in srgb, ${tokens.color.primary} 10%, transparent)` : tokens.surface.subtle,
									}}
								>
									<View row gap={6} style={{ alignItems: 'center' }}>
										<View
											style={{
												width: 12,
												height: 12,
												borderRadius: 3,
												background: PLAN_SHAPE_STYLES[state].bgColor,
												border: `1px ${PLAN_SHAPE_STYLES[state].borderDashed ? 'dashed' : 'solid'} ${PLAN_SHAPE_STYLES[state].borderColor}`,
											}}
										/>
										<View size={16} style={{ fontWeight: 600 }}>{counts[state]}</View>
									</View>
									<View size={11} color={tokens.text.secondary}>{label}</View>
								</button>
							);
						})}
					</View>

					{shown && (
						<View row gap={8} wrap style={{ alignItems: 'center' }}>
							<View flex size={12} color={tokens.text.secondary}>
								Seules ces formes sont mises en avant sur le plan.
							</View>

							{'unsupported' === shown && 0 < unsupportedShapes.length && (confirming
								? (
									<>
										<View size={12}>Créer {unsupportedShapes.length} polygone(s) libre(s) ?</View>
										<Button
											size="small"
											type="primary"
											onClick={() => {
												planImportStore.arbitrateMany(unsupportedShapes, 'kept');
												setConfirming(false);
												setIsolated(null);
											}}
										>
											Oui, tout garder
										</Button>
										<Button size="small" onClick={() => setConfirming(false)}>Annuler</Button>
									</>
								)
								: <Button size="small" onClick={() => setConfirming(true)}>Tout garder</Button>)}

							{'discarded' === shown && 0 < manualDiscards && (
								<Button
									size="small"
									onClick={() => {
										planImportStore.restoreManualDiscards();
										setIsolated(null);
									}}
								>
									Réintégrer les {manualDiscards} écartée(s) à la main
								</Button>
							)}

							<Button size="small" type="text" onClick={() => setIsolated(null)}>Tout afficher</Button>
						</View>
					)}

					{false === detection.source.readsText && (
						<Alert
							type="info"
							showIcon
							message="Les numéros ne sont pas lus sur une image ou un PDF scanné"
							description="Les formes seront créées sans numéro : saisissez ceux qui manquent dans la file des doutes, ou plus tard dans l’éditeur."
						/>
					)}

					{0 < detection.truncated && (
						<Alert
							type="warning"
							showIcon
							message={`${detection.truncated} forme(s) non affichées`}
							description="Le plan est trop dense pour tout afficher : resserrez la zone d’analyse pour voir le reste."
						/>
					)}
				</View>

				<View flex gap={16} style={{ overflowY: 'auto', padding: '16px 20px' }}>
					<View gap={6}>
						<View size={13} style={{ fontWeight: 600 }}>Type de POI</View>
						<SelectPlaceType
							projectUrn={projectUrn}
							placeholder={planImportStore.readyCount ? 'Plusieurs types (voir les options avancées)' : 'Choisir le type des formes importées'}
							value={planImportStore.sharedPlaceType}
							onChange={(value?: unknown) => value && planImportStore.applyPlaceType(String(value))}
						/>
					</View>

					{0 < planImportStore.readyCount && (
						<View gap={6}>
							<View size={13} style={{ fontWeight: 600 }}>POI déjà sur la carte</View>
							<PlanMatchChoice />
						</View>
					)}

					<View gap={8}>
						<View size={13} style={{ fontWeight: 600 }}>
							{remaining ? `À vérifier (${remaining} restant${1 < remaining ? 's' : ''})` : 'À vérifier'}
						</View>
						<PlanDoubtCard onShow={() => setFocusRequest(focusRequest + 1)} />
					</View>

					<View gap={6}>
						<button
							type="button"
							aria-expanded={advancedOpen}
							onClick={toggleAdvanced}
							style={{
								alignSelf: 'flex-start',
								display: 'flex',
								alignItems: 'center',
								gap: 6,
								padding: 0,
								border: 'none',
								background: 'none',
								font: 'inherit',
								fontSize: 12,
								color: tokens.text.muted,
								cursor: 'pointer',
							}}
						>
							<RightOutlined rotate={advancedOpen ? 90 : 0} style={{ fontSize: 10, transition: 'transform .2s ease' }} />
							Options avancées
						</button>

						{advancedOpen && (
							<View gap={6}>
								<View size={13} style={{ fontWeight: 600 }}>Ce qui sera importé</View>
								<PlanLayerList previewed={previewed} onPreview={togglePreview} />
							</View>
						)}
					</View>
				</View>
			</View>
		</View>
	);
});

export default PlanReviewStep;
